import React, { useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Modal,
  PageHeader,
  Space,
  Spin,
  Statistic,
  Tabs,
  Tag,
  notification,
} from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import VehicleStatus from '../../../core/enum/VehicleStatus';
import ChangeVehicleStatus from '../containers/ChangeVehicleStatus';
import VehicleStatusColorCode from '../../../core/enum/VehicleStatusColorCode';
import ViewVehicleDetails from '../containers/ViewVehicleDetails';
import EditVehicleForm from '../containers/EditVehicleForm';
import ViewVehicleSummery from '../containers/ViewVehicleSummery';
import { convertToCurrencyString } from '../../../utils/currencyUtil';
import {
  useDeleteVehicleMutation,
  useGetVehicleAuctionHistoryQuery,
  useGetVehicleQuery,
  useSortImagesMutation,
  useUpdateVehicleMutation,
} from '../../../app/api/vehicleApiSlice';
import BackButton from '../../../components/common/BackButton';
import Spinner from '../../../components/common/Spinner';
import ViewVehiclePhotos from '../containers/ViewVehiclePhotos';
import ViewVehicleInspection from '../containers/ViewVehicleInspection';
import ViewVehicleAuctionHistory from '../containers/ViewVehicleAuctionHistory';
import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import VehicleFile from '../../../core/models/VehicleFile';

interface IProps {
  children?: React.ReactNode;
  extra?: React.ReactNode;
}

const Content: React.FC<IProps> = ({ children, extra }: IProps) => (
  <div className='content'>
    <div className='main'>{children}</div>
    <div className='extra'>{extra}</div>
  </div>
);

const ViewVehicle: React.FC = () => {
  const { vehicleId } = useParams<{ vehicleId: any }>();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { data: vehicle, isLoading } = useGetVehicleQuery(vehicleId);
  const { data: vehicleAuctionHistory } = useGetVehicleAuctionHistoryQuery(vehicleId);
  const [sortImages, { isLoading: isSortImagesLoading, isError: isSortImagesError }] =
    useSortImagesMutation();
  const [deleteVehicle, { isError: isDeleteVehicleError }] = useDeleteVehicleMutation();
  const navigate = useNavigate();

  const extraContent = (
    <Space direction='vertical'>
      <Space
        style={{
          display: 'flex',
          width: 'max-content',
          justifyContent: 'flex-end',
        }}
      >
        <Statistic
          title='Starting Price'
          value={vehicle ? convertToCurrencyString(vehicle.startingPrice) : 'N/A'}
          style={{ marginRight: 32 }}
        />
        <Statistic
          title='Reserved Price'
          value={vehicle ? convertToCurrencyString(vehicle.reservePrice) : 'N/A'}
          style={{ marginRight: 32 }}
        />
      </Space>
      <Space
        style={{
          display: 'flex',
          width: 'max-content',
          justifyContent: 'flex-end',
        }}
      >
        <Statistic
          title='Sold Price'
          value={vehicle?.purchase ? convertToCurrencyString(vehicle?.purchase.soldPrice) : 'N/A'}
          style={{ marginRight: 32 }}
        />
        <Statistic
          title='Highest Bid'
          value={
            vehicle?.currentBid ? convertToCurrencyString(vehicle?.currentBid.bidPrice) : 'N/A'
          }
          style={{ marginRight: 32 }}
        />
        <Link
          to={
            vehicle?.currentBid?.buyer.id ? `/users/buyers/${vehicle?.currentBid?.buyer.id}` : '#'
          }
        >
          <Statistic
            title='Buyer'
            value={vehicle?.purchase ? vehicle?.purchase.buyer : 'N/A'}
            valueStyle={{ marginRight: 32, color: '#0162b1' }}
          />
        </Link>
      </Space>
    </Space>
  );

  const handleVehicleDelete = (vehicleId: string): void => {
    Modal.confirm({
      title: 'Delete Vehicle',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this vehicle?',
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk: async () => {
        deleteVehicle(vehicleId)
          .unwrap()
          .then((response) => {
            notification.success({
              message: 'Success',
              description: 'Vehicle deleted successfully',
            });

            navigate(-1);
          })
          .catch((error) => {
            console.log(error);
          });
      },
    });
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (!vehicle) {
    return <div>Vehicle not found!</div>;
  }

  const sortVehicleImages = (images: VehicleFile[]): void => {
    const data = { ...vehicle, sellerId: vehicle.seller?.id };
    data.images = images;

    sortImages(data)
      .unwrap()
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <>
      <BackButton />
      <PageHeader
        className='site-page-header-responsive'
        title={vehicle?.name}
        subTitle={
          <Link to={`/users/sellers/${vehicle?.seller?.id}`}>
            {vehicle?.seller?.companyName || vehicle?.seller?.personInCharge}
          </Link>
        }
        tags={
          <Tag color={vehicle ? VehicleStatusColorCode[vehicle?.status] : ''}>
            {vehicle ? VehicleStatus[vehicle?.status] : 'N/A'}
          </Tag>
        }
        extra={[
          <ChangeVehicleStatus
            vehicleId={vehicle.id}
            vehicleStatus={vehicle?.status}
            key='changeVehicleStatus'
          />,
        ]}
        footer={
          <Tabs
            defaultActiveKey='details'
            size='small'
            type='card'
            items={[
              {
                label: 'Vehicle Details',
                key: 'details',
                children: (
                  <Card>
                    <ViewVehicleDetails
                      layout='vertical'
                      vehicle={vehicle}
                      onEditClick={() => setDrawerVisible(true)}
                      onDeleteClick={() => handleVehicleDelete(vehicle.id)}
                    />
                    <Drawer
                      title='Update Vehicle Details'
                      width={600}
                      onClose={() => setDrawerVisible(false)}
                      open={drawerVisible}
                      bodyStyle={{ paddingBottom: 80 }}
                      destroyOnClose={true}
                      extra={
                        <Space>
                          <Button onClick={() => setDrawerVisible(false)}>Cancel</Button>
                          <Button
                            type='primary'
                            form='updateVehicleForm'
                            key='submit'
                            htmlType='submit'
                          >
                            Submit
                          </Button>
                        </Space>
                      }
                    >
                      <EditVehicleForm
                        vehicle={vehicle}
                        onSuccessfulSubmit={() => setDrawerVisible(false)}
                        drawerVisible={drawerVisible}
                      />
                    </Drawer>
                  </Card>
                ),
              },
              {
                label: 'Vehicle Photos',
                key: 'photos',
                children: (
                  <Card>
                    {isSortImagesLoading && (
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                        }}
                      />
                    )}
                    <ViewVehiclePhotos
                      vehicleId={vehicle.id}
                      images={vehicle.images}
                      sortable={true}
                      onSortEnd={(images: VehicleFile[] | undefined) => {
                        if (vehicle && images) {
                          sortVehicleImages(images);
                        }
                      }}
                    />
                  </Card>
                ),
              },
              {
                label: 'Grading Report',
                key: 'inspection',
                children: (
                  <Card>
                    <ViewVehicleInspection vehicle={vehicle} />
                  </Card>
                ),
              },
              {
                label: 'Auction History',
                key: 'auction history',
                children: (
                  <Card>
                    <ViewVehicleAuctionHistory vehicleAuctionHistory={vehicleAuctionHistory} />
                  </Card>
                ),
              }, // remember to pass the key prop
            ]}
          ></Tabs>
        }
      >
        <Content extra={extraContent}>
          <ViewVehicleSummery column={1} vehicle={vehicle} />
        </Content>
      </PageHeader>
    </>
  );
};

export default ViewVehicle;
